/*
 * Let's follow this strategy to name our enums:
 * 1. System name as prefix: ATS, Connect
 * 2. Next: Page name, e.g.: Home, SignIn, JobsList
 * 3. Next: DataTestIds
 *
 * For each attribute name:
 * 1. Next: Element Identifier, e.g.: submit-, email-, password-
 * 2. Next: Element type, e.g.: button, input, text, link
 *
 * For the value, we must follow this rule:
 * data-testIds should follow the pattern area-noun-component (ex. connect-express-interest-primary-button)
 * More context here: https://nimble-ed.slack.com/archives/C06DZ7FF3RQ/p1721922036641099
 */

export enum ATSCandidateBasicInfoDataTestIds {
  ADDRESS_LINE1_INPUT = 'ats-candidate-basic-info-address-line1-input',
  CELL_PHONE_INPUT = 'ats-candidate-basic-info-cell-phone-input',
  CITY_INPUT = 'ats-candidate-basic-info-city-input',
  COMPLETE_PROFILE_TITLE = 'ats-candidate-basic-info-complete-profile-title',
  ETHNICITY_SELECT = 'ats-candidate-basic-info-ethnicity-select',
  GENDER_SELECT = 'ats-candidate-basic-info-gender-select',
  SAVE_AND_NEXT_BUTTON = 'ats-candidate-basic-info-save-and-next-button',
  UPLOAD_RESUME_INPUT = 'ats-candidate-basic-info-upload-resume-input',
  ZIP_INPUT = 'ats-candidate-basic-info-zip-input',
}

export enum ATSCandidateCredentialsDataTestIds {
  ADD_CREDENTIALS_BUTTON = 'ats-candidate-credentials-add-credentials-button',
  CREDENTIAL_TYPE_SELECT = 'ats-candidate-credentials-credentials-type-select',
  CREDENTIAL_TYPE_SELECT_DROPDOWN_OPTION = 'ats-candidate-credentials-credentials-type-select-dropdown-option',
  CREDENTIAL_STATE_SELECT = 'ats-candidate-credentials-credential-state-select',
  CREDENTIAL_CATEGORY_SELECT = 'ats-candidate-credentials-credential-category-select',
  SAVE_ENTRY_BUTTON = 'ats-candidate-credentials-save-entry-button',
}

export enum ATSCandidateExamsDataTestIds {
  ADD_EXAMS_BUTTON = 'ats-candidate-exams-add-exam-button',
  EXAM_SELECT = 'ats-candidate-exams-exam-select',
  EXAM_SELECT_DROPDOWN_OPTION = 'ats-candidate-exams-exam-select-dropdown-option',
  SAVE_ENTRY_BUTTON = 'ats-candidate-exams-save-entry-button',
}

export enum ATSCandidateExperienceDataTestIds {
  ADD_WORK_EXPERIENCE_BUTTON = 'ats-candidate-experience-add-work-experience-button',
  ADVANCED_DEGREE_YES_INPUT = 'ats-candidate-experience-advanced-degree-yes-input',
  EMPLOYER_INPUT = 'ats-candidate-experience-employer-input',
  END_MONTH_DATETIME = 'ats-candidate-experience-end-month-datetime',
  END_YEAR_DATETIME = 'ats-candidate-experience-end-year-datetime',
  EXPERIENCE_TYPE_DROPDOWN = 'ats-candidate-experience-experience-type-dropdown',
  HEAR_ABOUT_US_SELECT = 'ats-candidate-experience-hear-about-us-select',
  JOB_DESCRIPTION_INPUT = 'ats-candidate-experience-job-description-input',
  JOB_TITLE_INPUT = 'ats-candidate-experience-job-title-input',
  MISSING_SECTIONS_MODAL = 'ats-candidate-experience-missing-sections-modal',
  REASON_FOR_LEAVING_INPUT = 'ats-candidate-experience-reason-for-leaving-input',
  SAVE_AND_COMPLETE_BUTTON = 'ats-candidate-experience-save-and-complete-button',
  SAVE_AND_NEXT_STEP_BUTTON = 'ats-candidate-experience-save-and-next-step-button',
  SAVE_ENTRY_BUTTON = 'ats-candidate-experience-save-entry-button',
  START_MONTH_DATETIME = 'ats-candidate-experience-start-month-datetime',
  START_YEAR_DATETIME = 'ats-candidate-experience-start-year-datetime',
  EDIT_EXPERIENCE_BUTTON = 'ats-candidate-experience-edit-experience-button',
}

export enum ATSCandidateExperienceModalDataTestIds {
  IGNORE_AND_PROCEED_BUTTON = 'ats-candidate-experience-modal-ignore-and-proceed-button',
  MODAL_CONTAINER = 'ats-candidate-experience-modal-container',
}

export enum ATSCandidateEducationDataTestIds {
  ADD_EDUCATION_BUTTON = 'ats-candidate-education-add-education-button',
  DEGREE_SELECT_DROPDOWN = 'ats-candidate-education-degree-select-dropdown',
  DEGREE_SELECT_DROPDOWN_OPTION = 'ats-candidate-education-degree-select-dropdown-option',
  SAVE_ENTRY_BUTTON = 'ats-candidate-education-save-entry-button',
}

export enum ATSCandidateHomeDataTestIds {
  PROFILE_UPDATED_TITLE = 'ats-candidate-home-profile-updated-title',
  REMINDER_HEADER_TITLE = 'ats-candidate-home-reminder-header-title',
  EDIT_APPLICATION_BUTTON = 'ats-candidate-home-edit-application-button',
  VIEW_MORE_ROLES_BUTTON = 'ats-candidate-home-view-more-roles-button',
}

export enum ATSCandidateLanguagesDataTestIds {
  ADD_LANGUAGES_BUTTON = 'ats-candidate-languages-add-languages-button',
  LANGUAGE_SELECT = 'ats-candidate-languages-language-select',
  LANGUAGE_SELECT_DROPDOWN_OPTION = 'ats-candidate-languages-language-select-dropdown-option',
  PROFICIENCY_SELECT = 'ats-candidate-languages-proficiency-select',
  PROFICIENCY_SELECT_DROPDOWN_OPTION = 'ats-candidate-languages-proficiency-select-dropdown-option',
  SAVE_ENTRY_BUTTON = 'ats-candidate-languages-save-entry-button',
  EDIT_LANGUAGES_BUTTON = 'ats-candidate-languages-edit-entry-button',
}

export enum ATSCandidatePreferencesDataTestIds {
  CATEGORIES_OF_INTEREST_MULTISELECT = 'ats-candidate-preferences-categories-of-interest-multiselect',
  COMPLETE_PROFILE_TEXT = 'ats-candidate-preferences-complete-profile-text',
  MAP_IMAGE = 'ats-candidate-preferences-map-image',
  SAVE_AND_NEXT_BUTTON = 'ats-candidate-preferences-save-and-next-button',
  STATE_PREFERENCES_MULTISELECT = 'ats-candidate-preferences-state-preferences-multiselect',
  TITLE_TEXT = 'ats-candidate-preferences-title-text',
}

export enum ATSCandidateReferencesDataTestIds {
  ADD_REFERENCES_BUTTON = 'ats-candidate-references-add-reference-button',
  FIRST_NAME_INPUT = 'ats-candidate-references-first-name-input',
  LAST_NAME_INPUT = 'ats-candidate-references-last-name-input',
  EMAIL_INPUT = 'ats-candidate-references-email-input',
  SAVE_ENTRY_BUTTON = 'ats-candidate-references-save-entry-button',
  PRINT = 'ats-candidate-references-print-button',
}

export enum ATSCustomCandidateFieldsDataTestIds {
  CONTAINER = 'ats-custom-candidate-fields-container',
  HEADING = 'ats-custom-candidate-fields-heading',
  FIELD_TITLE = 'ats-custom-candidate-fields-field-title',
  DOB_TITLE = 'ats-custom-candidate-fields-dob-title',
  DOB_SECTION = 'ats-custom-candidate-fields-dob-section',
  LIST_CONTAINER = 'ats-custom-candidate-fields-list-container',
  SSN_SECTION = 'ats-custom-candidate-fields-ssn-section',
  SSN_HEADER = 'ats-custom-candidate-fields-ssn-header',
  SSN_INPUT = 'ats-custom-candidate-fields-ssn-input',
  VIEW_SSN_BUTTON = 'ats-custom-candidate-fields-view-ssn-button',
  HIDE_SSN_BUTTON = 'ats-custom-candidate-fields-hide-ssn-button',
  CURRENCY_INPUT = 'ats-custom-candidate-fields-currency-input',
  TEXT_INPUT = 'ats-custom-candidate-fields-text-input',
  LINK_INPUT = 'ats-custom-candidate-fields-link-input',
}

export enum ATSCandidateListTestIds {
  FIRST_CANDIDATE_ROW = 'ats-first-candidate-row',
  FIRST_CANDIDATE_ROW_FURTHEST_STATUS = 'ats-first-candidate-row-furthest-status',
  FURTHEST_STATUS = 'ats-candidate-row-furthest-status',
  CANDIDATE_ROW = 'ats-candidate-row',
  CANDIDATE_ROW_FULL_NAME = 'ats-candidate-row-full-name',
  CANDIDATE_ROW_SCORECARD_ICON = 'ats-candidate-row-scorecard-icon',
  CANDIDATE_ROW_NOTE_ICON = 'ats-candidate-row-note-icon',
  CANDIDATE_ROW_DOCUMENT_ICON = 'ats-candidate-row-document-icon',
  CANDIDATE_ROW_JOB_TITLE = 'ats-candidate-row-job-title',
  CANDIDATE_APPLICATION = 'ats-candidate-row-application',
  DAYS_AGO_COUNT = 'ats-candidate-days-ago-count',
  CANDIDATE_APPLICATION_STATUS = 'ats-candidate-application-status',
  STATUS_SELECT = 'ats-candidate-status-select',
  STATUS_SELECT_CONNECT = 'ats-candidate-status-select-connect',
}

export enum ATSCandidateListBulkEmail {
  BULK_EMAIL_BUTTON = 'ats-candidate-list-bulk-email-buttonm',
  BULK_STATUS_CHANGE_BUTTON = 'ats-candidate-list-bulk-status-change-button',
  BULK_EMAIL_COUNT = 'ats-candidate-list-bulk-email-count',
  SELECT_ALL_CANDIDATES_BUTTON = 'ats-candidate-list-select-all-button',
  STATUS_CHANGE_OPTIONS = 'ats-candidate-list-status-change-options',
  INDIVIDUAL_STATUS_CHANGE_OPTION = 'ats-candidate-list-status-change-individual-option',
  BULK_EMAIL_SEND_NOW_BUTTON = 'ats-candidate-list-bulk-email-send-now-button',
  SELECT_TEMPLATE_SELECT = 'ats-candidate-list-bulk-email-select-template-select',
  BULK_EMAIL_SUBJECT = 'ats-candidate-list-bulk-email-subject',
}

export enum ATSCandidateTaskListTestIds {
  DELETE_BUTTON = 'ats-task-list-delete-button',
  FULL_PROFILE_TASK_TITLE = 'ats-full-profile-task-list-task-title',
  FULL_PROFILE_TASK_DUE_DATE = 'ats-full-profile-task-list-task-due-date',
  FULL_PROFILE_COMPLETE_ICON = 'ats-full-profile-task-list-complete-icon',
  FULL_PROFILE_HEADER = 'ats-full-profile-task-list-header',
  FULL_PROFILE_TASK_ROW = 'ats-full-profile-task-list-row',
  QUICKVIEW_HEADER = 'ats-quickview-task-list-header',
  QUICKVIEW_TASK_TITLE = 'ats-quickview-task-list-task-title',
  QUICKVIEW_TASK_DUE_DATE = 'ats-quickview-task-list-task-due-date',
  QUICKVIEW_COMPLETE_ICON = 'ats-quickview-task-list-complete-icon',
  QUICKVIEW_TASK_ROW = 'ats-quickview-task-list-row',
}

export enum ATSCandidatesListTestIds {
  ERROR_TOAST = 'ats-task-list-delete-button',
}

export enum ATSCandidatesListFilterTestIds {
  CANDIDATE_COUNT = 'ats-candidates-list-candidate-count',
  CANDIDATES_LIST_LANGUAGE_CLEAR_ALL = 'ats-candidates-list-language-clear-all',
  CANDIDATES_LIST_LANGUAGE_FILTER = 'ats-candidates-list-candidate-language-filter',
  CANDIDATES_LIST_LANGUAGE_OPTION = 'ats-candidates-list-language-option',
  CANDIDATE_PREFERENCES_EARLY_CHILDHOOD_GRADE_OPTION = 'ats-candidates-list-preferences-grades-early-childhood-option',
  CANDIDATE_PREFERENCES_GRADES_CLEAR_ALL_BUTTON = 'ats-candidates-list-preferences-grades-clear-all-button',
  CANDIDATE_PREFERENCES_GRADES_FILTER = 'ats-candidates-list-candidate-preferences-grades',
  CANDIDATE_PREFERENCES_SCHOOL_TYPES_FILTER = 'ats-candidates-list-candidate-preferences-school-types',
  CANDIDATE_PREFERENCES_SECTION = 'ats-candidates-list-candidate-preferences-section',
  FILTER = 'ats-candidates-list-filter-header',
  FILTER_OPTION = 'ats-candidates-list-filter-option',
  SEARCH_BAR = 'ats-candidates-list-search-bar',
  SEARCH_BUTTON = 'ats-candidates-list-search-button',
  SORT_BY_NAME_BUTTON = 'ats-candidates-list-sort-by-name-button',
  STATUS_CLEAR_ALL_BUTTON = 'ats-candidates-list-status-clear-all-button',
}

export enum ATSConnectImportCandidateFormTestIds {
  CANDIDATE_EMAIL = 'ats-connect-integration-candidate-email',
  CANDIDATE_HAS_ALREADY_APPLIED_SECTION = 'ats-connect-integration-candidate-has-already-applied-section',
  CANDIDATE_HAS_EXISTING_HIRING_REQUEST_SECTION = 'ats-connect-integration-candidate-has-existing-hiring-request-section',
  CANDIDATE_HAS_NOT_APPLIED_INTERNAL_NOTE_SECTION = 'ats-connect-integration-candidate-has-not-applied-internal-note-section',
  CANDIDATE_NAME = 'ats-connect-integration-candidate-name',
  CONTACT_LIST = 'ats-connect-contact-list',
  CONTACT_OPTIONS = 'ats-connect-integration-contact-methods-options',
  CONTACT_METHODS_SUCCESS_PAGE = 'ats-connect-contact-methods-success-page',
  IMPORT_CANDIDATE_BUTTON = 'ats-connect-integration-import-candidate-button',
  JOB_COMBOBOX = 'ats-connect-import-candidate-job-combobox',
  TITLE = 'ats-connect-integration-title',
  VIEW_CANDIDATES_BUTTON = 'ats-connect-integration-view-candidates-button',
  EXISTING_HIRING_REQUEST_TITLE = 'ats-connect-integration-existing-hiring-request-title',
  EXISTING_HIRING_REQUEST_FIRST_BULLET = 'ats-connect-integration-existing-hiring-request-first-bullet',
  EXISTING_HIRING_REQUEST_SECOND_BULLET = 'ats-connect-integration-existing-hiring-request-second-bullet',
  EXISTING_APPLICATION_TITLE = 'ats-connect-integration-existing-application-title',
  EXISTING_APPLICATION_FIRST_BULLET = 'ats-connect-integration-existing-application-first-bullet',
  EXISTING_APPLICATION_SECOND_BULLET = 'ats-connect-integration-existing-application-second-bullet',
  HIRING_REQUEST_FORM_LOADER = 'ats-connect-integration-hiring-request-form-loader',
  HIRING_REQUEST_FORM_ALERT = 'ats-connect-integration-hiring-request-form-alert',
  SUCCESS_TITLE = 'ats-connect-integration-success-title',
  SUCCESS_TEXT = 'ats-connect-integration-success-text',
  SUCCESS_CANDIDATE_NAME = 'ats-connect-integration-success-candidate-name',
  SUCCESS_CANDIDATE_EMAIL = 'ats-connect-integration-success-candidate-email',
  SUCCESS_CANDIDATE_INTERNAL_NOTE = 'ats-connect-integration-success-candidate-internal-note',
  SUCCESS_ROLE_TITLE = 'ats-connect-integration-success-role-title',
  SUCCESS_ROLE_LOCATION = 'ats-connect-integration-success-role-location',
  SUCCESS_NEXT_STEPS = 'ats-connect-integration-success-next-steps',
}

export enum ATSDataFFTDataTestIds {
  APPLICATION_FIELD_SELECT = 'ats-data-fft-application-field-select',
  CANDIDATE_PROFILE_SELECT = 'ats-data-fft-candidate-profile-select',
  COLUMN_NAME_INPUT = 'ats-data-fft-column-name-input',
  COLUMN_ROW = 'ats-data-fft-column-row',
  DATA_FORMAT_SELECT = 'ats-data-fft-data-format-select',
  DATA_SOURCE_SELECT = 'ats-data-fft-data-source-select',
  DELETE_ROW_BUTTON = 'ats-data-fft-delete-row-button',
  DOWNLOAD_TEST_BUTTON = 'ats-data-fft-download-test-button',
  FIELD_SELECT = 'ats-data-fft-field-select',
  NEW_COLUMN_BUTTON = 'ats-data-fft-new-column-button',
  NEW_DATA_TRANSFER_BUTTON = 'ats-data-fft-new-data-transfer-button',
  SAVE_BUTTON = 'ats-data-fft-save-button',
  DATA_MAPPING_SAVE = 'ats-data-fft-data-mapping-save-button',
}

export enum ATSDistrictHomeDataTestIds {
  GREETING_TEXT = 'ats-home-greeting-text',
  HIGH_RETENTION = 'ats-home-high-retention',
  HIGHLY_SKILLS_TEXT = 'ats-home-highly-skills-text',
  TOP_CANDIDATE_CONTAINER = 'ats-home-top-candidate-container',
  TOP_CANDIDATE_JOB_TEXT = 'ats-home-top-candidate-job-text',
  TOP_CANDIDATE_NAME_TEXT = 'ats-home-top-candidate-name-text',
}

export enum ATSDistrictJobListAlertBannerDataTestId {
  ALERT_BANNER = 'ats-district-job-list-alert-banner',
}

export enum ATSDistrictJobListDataTestIds {
  JOB_ROW = 'ats-district-job-board-job-row',
  JOB_TITLE = 'ats-district-job-board-job-row-job-title',
  COLUMN_HEADER = 'ats-district-job-board-job-list-column-header',
  COLUMN_NAME = 'ats-district-job-board-job-list-column-header-column-name-text',
  INTRO_TEXT = 'ats-district-job-board-job-list-intro-text',
  REQUISITION_ROW = 'ats-district-job-board-job-list-requisition-row',
  REQUISITION_JOB_TITLE = 'ats-district-job-board-job-list-requisition-title',
}

export enum ATSDistrictJobsListFiltersDataTestIds {
  CATEGORY_FILTER = 'ats-district-job-list-category-filter-dropdown',
  EMPLOYMENT_TYPE_FILTER = 'ats-district-job-list-employment-type-filter-dropdown',
  GRADE_LEVEL_FILTER = 'ats-district-job-list-grade-level-filter-dropdown',
  SCHOOL_FILTER = 'ats-district-job-list-school-filter-dropdown',
  SEARCH_INPUT = 'ats-district-job-list-search-input',
  SEARCH_SUBMIT_BUTTON = 'ats-district-job-list-filter-search-submit-button',
}

export enum ATSDistrictJobsListCategoryFilterDataTestIds {
  ALL_CATEGORIES_OPTION = 'ats-district-job-list-category-filter-all-categories-option',
  CATEGORY_OPTION = 'ats-district-job-list-category-filter-categories-option',
  SUBCATEGORY_OPTION = 'ats-district-job-list-category-filter-subcategory-option',
}

export enum ATSDistrictJobsListGradeLevelFilterDataTestIds {
  ALL_GRADES_OPTION = 'ats-district-job-list-grade-level-filter-all-grades-option',
  GRADE_OPTION = 'ats-district-job-list-category-filter-grade-option',
}

export enum ATSJobClosureNotificationTestIds {
  ADD_NOTIFICATION_BUTTON = 'ats-job-closure-add-notification-button',
  ALERT_BANNER = 'ats-job-closure-alert-banner',
  APPLICATION_STATUS_ID_ERROR_TEXT = 'ats-job-closure-modal-application-status-id-error',
  DELETE_NOTIFICATION_BUTTON = 'ats-job-closure-delete-notifications-button',
  EDIT_NOTIFICATION_BUTTON = 'ats-job-closure-edit-notifications-button',
  HEADING = 'ats-job-closure-heading',
  JOB_CLOSURE_SECTION = 'ats-job-closure-notifications-section',
  MODAL = 'ats-job-closure-modal',
  MODAL_PRIMARY_BUTTON = 'ats-job-closure-modal-primary-button',
  MODAL_TITLE = 'ats-job-closure-modal-title',
  THRESHOLD_ERROR_TEXT = 'ats-job-closure-modal-threshold-error',
  SUBSCRIBERS_ERROR_TEXT = 'ats-job-closure-modal-subscribers-error',
  UPDATE_SUCCESS_SNACKBAR = 'ats-job-closure-update-success-snackbar',
  DELETE_SUCCESS_SNACKBAR = 'ats-job-closure-delete-success-snackbar',
}

export enum ATSJobViewDataTestIds {
  APPLY_BUTTON = 'ats-job-view-apply-button',
  APPLY_LINK = 'apply-link',
  APPLY_MODAL = 'ats-job-view-apply-modal',
  DETAILS_CONTAINER = 'ats-job-view-details-container',
  TITLE_TEXT = 'ats-job-view-title-text',
  POST_BUTTON = 'ats-job-view-post-button',
  SHARE_BUTTON = 'ats-job-view-share-button',
  EDIT_BUTTON = 'ats-job-view-edit-button',
}
export enum ATSJobEditTestIds {
  SAVE_PREVIEW_BUTTON = 'ats-job-edit-save-preview-button',
  LOCATIONS_MODAL_OPEN = 'ats-job-edit-locations-modal-open-button',
  LOCATIONS_MODAL_SAVE = 'ats-job-edit-locations-modal-save-button',
  SCHOOL_VACANCIES_RADIO = 'ats-job-edit-school-vacancies-radio',
  DISTRICT_VACANCIES_RADIO = 'ats-job-edit-district-vacancies-radio',
  INCREMENT_VACANCY_BUTTON = 'ats-job-edit-increment-vacancy-button',
  DECREMENT_VACANCY_BUTTON = 'ats-job-edit-decrement-vacancy-button',
  AUTO_TAGS_SAVE_BUTTON = 'ats-job-edit-auto-tags-modal-save-button',
  QUESTION_SAVE_BUTTON = 'ats-job-edit-question-modal-save-button',
  QUESTION_SET_SAVE_BUTTON = 'ats-job-edit-question-set-modal-save-button',
  SUBMIT_BUTTON = 'ats-job-edit-submit-button',
  TITLE_INPUT = 'ats-job-edit-title-input',
  SUCCESS_SNACKBAR = 'ats-job-edit-success-snackbar',
  ERROR_SNACKBAR = 'ats-job-edit-error-snackbar',
}

export enum ATSJobsListDataTestIds {
  COUNT_TEXT = 'ats-jobs-list-count-text',
  FILTER_CONTAINER = 'ats-jobs-list-filter-container',
  ROW_CONTAINER = 'ats-jobs-list-row-container',
  ROW_DATE_TEXT = 'ats-jobs-list-row-date-text',
  ROW_TITLE_TEXT = 'ats-jobs-list-row-title-text',
  SEARCH_INPUT = 'ats-jobs-list-search-input',
  CATEGORY_DROPDOWN = 'ats-jobs-list-categories-dropdown',
  CATEGORY_CLEARALL = 'ats-jobs-list-categories-clearall',
  CATEGORY_OPTION = 'ats-jobs-list-categories-option',
  GRADES_DROPDOWN = 'ats-jobs-list-grades-dropdown',
  GRADES_CLEARALL = 'ats-jobs-list-grades-clearall',
  GRADES_ALL_ELEMENTARY = 'ats-jobs-list-grades-all-elementary',
  NEW_JOB_BUTTON = 'ats-jobs-list-new-job-button',
  NEW_TEMPLATE_BUTTON = 'ats-jobs-list-new-template-button',
  SCHOOL_NAME = 'ats-jobs-list-school-name',
  STYLED_LIST_DROPDOWN = 'ats-jobs-list-styled-list-dropdown',
  STYLED_LIST_CLEARALL = 'ats-jobs-list-styled-list-clearall',
  STYLED_LIST_OPTION = 'ats-jobs-list-styled-list-option',
  STYLED_LIST_HIRING_SEASON = 'hiring-season',
  STYLED_LIST_INTERNAL_EXTERNAL = 'internal-external',
  SCHOOLS_LIST = 'ats-jobs-list-schools-list-cell',
  TOGGLE_BUTTONS = 'ats-jobs-list-template-job-toggle-button',
}

export enum ATSJobsListQuickActionTestIds {
  ARCHIVE_JOB = 'ats-jobs-list-quick-action-archive-job',
  CLOSE_JOB = 'ats-jobs-list-quick-action-close-job',
  DUPLICATE_JOB = 'ats-jobs-list-quick-action-duplicate-job',
  EDIT_JOB = 'ats-jobs-list-quick-action-edit-job',
  RETURN_JOB = 'ats-jobs-list-quick-action-return-job',
  MERGE_JOB = 'ats-jobs-list-quick-action-merge-job',
  SHARE_JOB = 'ats-jobs-list-quick-action-share-job',
  VIEW_JOB = 'ats-jobs-list-quick-action-view-job',
  DELETE_TEMPLATE = 'ats-jobs-list-quick-action-delete-template',
  EDIT_TEMPLATE = 'ats-jobs-list-quick-action-edit-template',
  JOB_FROM_TEMPLATE = 'ats-jobs-list-quick-action-job-from-template',
  TEMPLATE_FROM_JOB = 'ats-jobs-list-quick-action-template-from-job',
}

export enum ATSNewJobTemplateModalTestIds {
  JOB_TITLE_INPUT = 'ats-new-job-job-name-input',
  TEMPLATE_TITLE_INPUT = 'ats-new-template-name-input',
  COMBO_BOX = 'ats-new-job-template-modal-combo-box',
  CANCEL_BUTTON = 'ats-new-job-template-modal-cancel-button',
  CREATE_JOB_BUTTON = 'ats-new-job-template-modal-create-job-button',
  CONVERT_JOB_BUTTON = 'ats-new-job-template-modal-convert-job-button',
  CREATE_TEMPLATE_BUTTON = 'ats-new-job-template-modal-create-template-button',
}
export enum ATSNewMergeJobModalTestIds {
  COMBO_BOX = 'ats-merge-job-modal-combo-box',
}

export enum ATSNavbarDataTestIds {
  CANDIDATES_BUTTON = 'ats-navbar-candidates-button',
  CANDIDATES_BUTTON_BY_POSTING = 'ats-navbar-candidates-button-by-posting',
  CANDIDATES_BUTTON_VIEW_ALL = 'ats-navbar-candidates-button-view-all',
  DATA_BUTTON = 'ats-navbar-data-button',
  JOBS_DROPDOWN = 'ats-navbar-jobs-dropdown',
  JOBS_LINK = 'ats-navbar-jobs-link',
  PROSPECTS_BUTTON = 'ats-navbar-prospects-button',
  TEMPLATES_LINK = 'ats-navbar-templates-link',
  PIPELINE_BUTTON = 'ats-navbar-pipeline-button',
  USER_NAME = 'ats-navbar-user-name-dropdown',
  HIRING_REQUESTS_BUTTON = 'ats-navbar-hiring-requests-button',
}

export enum ATSQuickProfileApplicationSection {
  EXAMS_EXPANDER = 'ats-quick-profile-exams-expander',
}

export enum ATSReviewApplicationTestIds {
  RESUME_FILE_TITLE = 'ats-experience-resume-file-title',
  SUBMIT_APPLICATION_BUTTON = 'ats-experience-submit-application-button',
}

export enum ATSSignInDataTestIds {
  EMAIL_INPUT = 'ats-sign-in-email-input',
  PASSWORD_INPUT = 'ats-sign-in-password-input',
  SUBMIT_BUTTON = 'ats-sign-in-submit-button',
}

export enum ATSSignUpDataTestIds {
  ACTIVATE_BUTTON = 'ats-sign-up-activate-button',
  EMAIL_CONFIRM_INPUT = 'ats-sign-up-email-confirm-input',
  EMAIL_INPUT = 'ats-sign-up-email-input',
  ERROR_TEXT = 'ats-sign-up-error-text',
  FIRST_NAME_INPUT = 'ats-sign-up-first-name-input',
  GOOGLE_SIGN_UP_BUTTON = 'ats-sign-up-google-sign-up-button',
  LAST_NAME_INPUT = 'ats-sign-up-last-name-input',
  PASSWORD_CONFIRM_INPUT = 'ats-sign-up-password-confirm-input',
  PASSWORD_INPUT = 'ats-sign-up-password-input',
  SIGN_UP_CONTAINER = 'ats-sign-up-container',
  SUBMIT_BUTTON = 'ats-sign-up-submit-button',
}

export enum ATSStateJobBoardAlertsModalDataTestIds {
  CATEGORY_DROPDOWN = 'ats-state-job-board-alerts-modal-category-dropdown',
  ORGANIZATION_DROPDOWN = 'ats-state-job-board-alerts-modal-organization-dropdown',
}

export enum ATSStateJobBoardFiltersDataTestIds {
  CATEGORY_FILTER = 'ats-state-job-board-category-filter-dropdown',
  EMPLOYMENT_TYPE_FILTER = 'ats-state-job-board-employment-type-filter-dropdown',
  GRADE_LEVEL_FILTER = 'ats-state-job-board-grade-level-filter-dropdown',
  ORGANIZATION_FILTER = 'ats-state-job-board-organization-filter-dropdown',
  SCHOOL_FILTER = 'ats-state-job-board-school-filter-dropdown',
  SEARCH_INPUT = 'ats-state-job-board-search-input',
  START_DATE_FILTER = 'ats-state-job-board-start-date-filter-dropdown',
  LOCATION_FILTER = 'ats-state-job-board-location-filter-input',
  DISTANCE_FILTER = 'ats-state-job-board-distance-filter-input',
  LOCATION_DISTANCE_KEYWORD_SUBMIT_BUTTON = 'ats-state-job-board-location-distance-keyword-submit-button',
  SEARCH_SUBMIT_BUTTON = 'ats-state-job-board-header-search-submit-button',
}

export enum ATSStateJobBoardHeaderDataTestIds {
  JOB_COUNT = 'ats-state-job-board-header-job-count-text',
  SEARCH_INPUT = 'ats-state-job-board-header-search-input',
  SEARCH_SUBMIT_BUTTON = 'ats-state-job-board-header-search-submit-button',
}

export enum ATSStateJobBoardJobListDataTestIds {
  JOB_ROW = 'ats-state-job-board-job-row',
  JOB_TITLE = 'ats-state-job-board-job-row-job-title',
}

export enum ATSStateJobBoardTalentModalDataTestIds {
  NO_THANKS_BUTTON = 'ats-state-job-board-talent-modal-no-thanks-button',
}

export enum ATSTemplatesListDataTestIds {
  SEARCH_INPUT = 'ats-templates-list-search-input',
  ROW_TITLE_TEXT = 'ats-templates-list-row-title-text',
}

export enum ATSVacancyDashboardDataTestIds {
  CATEGORIES_MULTISELECT = 'ats-vacancy-dashboard-categories-multiselect',
  CLEAR_ALL_BUTTON = 'ats-vacancy-dashboard-clear-all-button',
  DOWNLOAD_RAW_DATA_BUTTON = 'ats-vacancy-dashboard-download-raw-data-button',
  DROPDOWN_CLEAR_BUTTON = 'ats-vacancy-dashboard-dropdown-clear-button',
  DROPDOWN_SELECT_ALL_BUTTON = 'ats-vacancy-dashboard-dropdown-select-all-button',
  HIRING_SEASON_MULTISELECT = 'ats-vacancy-dashboard-hiring-season-multiselect',
  JOB_POSTINGS_MULTISELECT = 'ats-vacancy-dashboard-job-postings-multiselect',
  SCHOOLS_MULTISELECT = 'ats-vacancy-dashboard-schools-multiselect',
  VACANCY_ROW = 'ats-vacancy-dashboard-vacancy-row',
  VACANCY_ROW_CANDIDATE_TITLE = 'ats-vacancy-dashboard-vacancy-candidate-title',
  VACANCY_ROW_JOB_TITLE = 'ats-vacancy-dashboard-vacancy-row-title',
}

export enum ATSPipelineMetricsDashboardDataTestIds {
  APPLICANT_VOLUME_GRAPH = 'ats-pipeline-metrics-dashboard-applicant-volume-graph',
}

export enum ATSCandidateQuickviewTestIds {
  DISTRICT_APPLICATION_QUICKVIEW = 'ats-candidate-list-district-application-quickview',
  DISTRICT_APPLICATION_QUICKVIEW_CANDIDATE_NAME = 'ats-candidate-list-district-application-quickview-candidate-name',
  SCHOOL_TOOLTIP_TEXT_ROW = 'ats-candidate-list-school-tooltip-text-row',
  SCHOOL_VISIBILITY_COUNT = 'ats-candidate-list-school-visibility-count',
}

export enum ATSInternalCandidateNoteDataTestIds {
  DELETE_BUTTON = 'ats-internal-candidate-note-delete-button',
  MENTIONS_INPUT = 'ats-internal-candidate-note-mentions-input',
  MENTION = 'ats-internal-candidate-note-mention',
  ADD_NOTE_BUTTON = 'ats-internal-candidate-note-add-note-button',
  EXISTING_NOTE_ROW = 'ats-internal-candidate-note-existing-note-row',
  EXISTING_NOTE_BULLET_POINT = 'ats-internal-candidate-note-existing-note-bullet-point',
  EXISTING_NOTE_TEXT = 'ats-internal-candidate-note-existing-note-text',
  EXISTING_NOTE_SIGNATURE = 'ats-internal-candidate-note-existing-note-signature',
  MENTION_USER_SUGGESTION = 'ats-internal-candidate-note-mention-user-suggestion',
  FULL_PROFILE_HEADER = 'ats-internal-candidate-note-district-application-header',
  DISTRICT_APPLICATION_QUICKVIEW_HEADER = 'ats-internal-candidate-note-district-application-quickview-header',
}

export enum ATSBottleneckNotifications {
  BOTTLENECK_HEADER = 'ats-bottleneck-notifications-header',
  BOTTLENECK_LIST = 'ats-bottleneck-notifications-list',
  BOTTLENECK_ROW = 'ats-bottleneck-notifications-row',
}

export enum ATSCandidateScorecardDataTestIds {
  FULL_PROFILE_ADD_VIEW_BUTTON = 'ats-candidate-full-profile-add-scorecard-button',
  QUICKVIEW_ADD_VIEW_BUTTON = 'ats-candidate-quickview-add-scorecard-button',
  MODAL_HEADER = 'ats-candidate-scorecard-modal-header',
  LIST = 'ats-candidate-scorecard-list',
  LIST_FOOTER = 'ats-candidate-scorecard-list-footer',
  LIST_CREATE_BUTTON = 'ats-candidate-scorecard-list-create-button',
  LIST_REQUEST_BUTTON = 'ats-candidate-scorecard-list-request-button',
  LIST_SECTION_HEADER = 'ats-scorecard-list-section-header',
  EDITOR = 'ats-candidate-scorecard-editor',
  EDITOR_CREATE_HEADER_TEXT = 'ats-candidate-scorecard-editor-create-header-text',
  EDITOR_DROPDOWN = 'ats-candidate-scorecard-editor-dropdown',
  SUBMIT_BUTTON = 'ats-candidate-scorecard-submit-button',
  SUBMIT_ERROR_MESSAGE = 'ats-candidate-scorecard-submit-error-message',
  TITLE_TEXT = 'ats-scorecard-editor-title-text',
  TEXT_ANSWER_MENTIONS_INPUT = 'ats-scorecard-create-text-answer-mentions-input',
  RATING_ANSWER_INPUT = 'ats-scorecard-create-rating-answer-input',
  RATING_ANSWER_SCALE = 'ats-scorecard-create-rating-answer-scale',
  MULTIPLE_CHOICE_ANSWER_OPTION = 'ats-scorecard-multiple-choice-answer-option',
  FINAL_RECOMMENDATION_ANSWER_OPTION = 'ats-scorecard-final-recommendation-answer-option',
  FINAL_RECOMMENDATION_ANSWER_SUBTEXT = 'ats-scorecard-final-recommendation-answer-subtext',
  SCORECARD_EDITOR_QUESTION_TEXT = 'ats-scorecard-editor-question-text',
  CUMULATIVE_SCORE = 'ats-scorecard-editor-cumulative-score-display',
  EXISTING_CARD_SUBMITTED_BY_TEXT = 'ats-scorecard-existing-card-submitted-by-text',
  EXISTING_CARD = 'ats-scorecard-existing-card',
  EXISTING_CARD_TITLE = 'ats-scorecard-existing-card-title',
  REQUEST_MODAL = 'ats-scorecard-request-modal',
  REQUEST_MODAL_SELECT_CARD_HEADER_TEXT = 'ats-scorecard-request-modal-select-card-header-text',
  REQUEST_MODAL_SCORECARD_DROPDOWN = 'ats-scorecard-request-modal-scorecard-dropdown',
  REQUEST_MODAL_RECIPIENTS_DROPDOWN = 'ats-scorecard-request-modal-recipients-dropdown',
  REQUEST_MODAL_ADD_RECIPIENT_BUTTON = 'ats-scorecard-request-modal-add-recipient-button',
  REQUEST_MODAL_DELETE_RECIPIENT_BUTTON = 'ats-scorecard-request-modal-delete-recipient-button',
  REQUEST_MODAL_RECIPIENT_HEADER = 'ats-scorecard-request-modal-select-recipient-header-text',
  REQUEST_MODAL_RECIPIENT_SUBTEXT = 'ats-scorecard-request-modal-select-recipient-subtext',
  REQUEST_MODAL_ADD_NOTE_HEADER = 'ats-scorecard-request-modal-add-note-header-text',
  REQUEST_MODAL_ADD_NOTE_TEXTAREA = 'ats-scorecard-request-modal-add-note-textarea',
  REQUEST_MODAL_ADD_NOTE_SUBTEXT = 'ats-scorecard-request-modal-add-note-subtext',
  REQUEST_MODAL_ERROR_TEXT = 'ats-scorecard-request-modal-error-text',
  REQUEST_MODAL_SAVE_BUTTON = 'ats-scorecard-request-modal-save-button',
  REQUEST_MODAL_CANCEL_BUTTON = 'ats-scorecard-request-modal-cancel-button',
  REQUEST_MODAL_FOOTER_CONTAINER = 'ats-scorecard-request-modal-footer-container',
  REQUEST_MODAL_BACK_BUTTON = 'ats-scorecard-request-modal-back-button',
  PENDING_REQUESTS_HEADER = 'ats-scorecard-modal-pending-requests-header',
  PENDING_REQUEST_ROW = 'ats-scorecard-modal-pending-request-row',
}

export enum ATSCandidateTagDataTestIds {
  HEADER = 'ats-candidate-tag-header',
  LIST_CONTAINER = 'ats-candidate-tag-container',
  ITEM = 'ats-candidate-tag-item',
  LABEL = 'ats-candidate-tag-label',
  REMOVE_ICON = 'ats-candidate-tag-remove-icon',
  DROPDOWN = 'ats-candidate-tag-dropdown',
  DROPDOWN_INPUT = 'ats-candidate-tag-dropdown-input',
  DROPDOWN_LIST_ITEM = 'ats-candidate-tag-dropdown-list-item',
  DROPDOWN_LIST_ITEM_TAG = 'ats-candidate-tag-dropdown-list-item-tag',
}

export enum ATSAttachmentsDataTestIds {
  HEADER = 'ats-candidate-attachments-header',
  CONTAINER = 'ats-candidate-attachments-container',
  SEND_FORM_ICON = 'ats-candidate-attachments-send-form-icon',
  SEND_FORM_TOOLTIP_SPAN = 'ats-candidate-attachments-send-form-tooltip',
  HELLOSIGN_MODAL_BODY = 'ats-candidate-attachments-hellosign-modal-body',
  HELLOSIGN_MODAL_PACKET_SELECT = 'ats-candidate-attachments-hellosign-modal-packet-select',
  HELLOSIGN_MODAL_PACKET_SELECT_LABEL = 'ats-candidate-attachments-hellosign-modal-packet-select-label',
  HELLOSIGN_MODAL_FORM_SELECT = 'ats-candidate-attachments-hellosign-modal-form-select',
  HELLOSIGN_MODAL_FORM_SELECT_LABEL = 'ats-candidate-attachments-hellosign-modal-form-select-label',
  HELLOSIGN_MODAL_DUE_DATE_HEADER = 'ats-candidate-attachments-hellosign-modal-due-date-header',
  HELLOSIGN_MODAL_CC_SOMEONE_HEADER = 'ats-candidate-attachments-hellosign-modal-cc-someone-header',
  HELLOSIGN_MODAL_CC_SOMEONE_SELECT = 'ats-candidate-attachments-hellosign-modal-cc-somone-select',
  HELLOSIGN_MODAL_NOTIFY_CANDIDATE_TOGGLE = 'ats-candidate-attachments-hellosign-modal-notify-candidate-toggle',
  HELLOSIGN_MODAL_SEND_FORMS_BUTTON = 'ats-candidate-attachments-hellosign-modal-send-forms-button',
  PENDING_FORMS_CONTAINER = 'ats-candidate-attachments-pending-forms-container',
  PREPARING_FORM = 'ats-candidate-attachments-preparing-form',
  PREPARING_FORM_TEXT = 'ats-candidate-attachments-preparing-form-text',
  PENDING_FORM = 'ats-candidate-attachments-pending-form',
  PENDING_FORM_TEXT = 'ats-candidate-attachments-pending-form-text',
  PENDING_FORM_CANCEL_BUTTON = 'ats-candidate-attachments-pending-form-cancel-button',
  PENDING_FORM_ATTACHMENT_ICON = 'ats-candidate-attachments-pending-form-attachment-icon',
  PENDING_FORM_EXPAND_BUTTON = 'ats-candidate-attachments-pending-form-expand-button',
  PENDING_FORM_SIGNER_ROW = 'ats-candidate-attachments-pending-form-signer-row',
  PENDING_FORM_SIGNER_NAME = 'ats-candidate-attachments-pending-form-signer-name',
  PENDING_FORM_STATUS = 'ats-candidate-attachments-pending-form-status',
  DELETE_ERROR_SNACKBAR = 'ats-candidate-attachments-delete-error-snackbar',
}

export enum ATSJobRequestUpdateTestIds {
  APPROVAL_PROMPT_TEXT = 'ats-job-request-update-approval-prompting-text',
  APPROVAL_TOGGLE_BUTTONS = 'ats-job-request-update-approval-toggle-buttons',
  CATEGORIES = 'ats-job-request-update-job-details-categories',
  CREATE_SUBFORM = 'ats-job-request-update-create-subform',
  DENIAL_TOGGLE_BUTTONS = 'ats-job-request-update-denial-toggle-buttons',
  DETAILS_CONTAINER = 'ats-job-request-update-details-container',
  GRADES = 'ats-job-request-update-job-details-grades',
  INNER_FORM_CLOSE_BUTTON = 'ats-job-request-update-grey-box-close-button',
  INNER_FORM_CONTAINER = 'ats-job-request-internal-form-container',
  INTERNAL_ROLE_NOTES = 'ats-job-request-update-job-details-internal-role-notes',
  LOADING_SPINNER = 'ats-job-request-update-loading-spinner',
  LOCATION = 'ats-job-request-update-job-details-location',
  MERGE_ENTER_HELP_TEXT = 'ats-job-request-update-merge-enter-help-text',
  MERGE_ERROR_TEXT = 'ats-job-request-update-merge-error-text',
  REQUEST_HELP_TEXT = 'ats-job-request-update-merge-help-text',
  MERGE_SUBFORM = 'ats-job-request-update-merge-subform',
  MODAL = 'ats-job-request-update-modal',
  MODAL_CLOSE_BUTTON = 'ats-job-request-update-modal-close-button',
  MODAL_TITLE = 'ats-job-request-update-modal-title',
  PRIMARY_APPROVE_BUTTON = 'ats-job-request-update-primary-approve-button',
  PRIMARY_DENIAL_BUTTON = 'ats-job-request-update-primary-denial-button',
  REQUESTER = 'ats-job-request-update-job-details-requester',
  REQUEST_TITLE = 'ats-job-request-update-request-title',
  REQUISITION_NUMBER = 'ats-job-request-update-job-details-requisition-number',
  RETURN_TEXT_BOX = 'ats-job-request-update-return-note-text-box',
  RETURN_TEXT_BOX_ERROR_MESSAGE = 'ats-job-request-update-return-text-box-error-message',
  SECONDARY_APPROVE_BUTTON = 'ats-job-request-update-secondary-approve-button',
  SECONDARY_DENIAL_BUTTON = 'ats-job-request-update-secondary-denial-button',
  SUCCESS_MESSAGE = 'ats-job-request-update-success-message',
  SUCCESS_BACK_TO_JOBS_LIST_BUTTON = 'ats-job-request-update-success-back-to-jobs-list-button',
  SUCCESS_EDIT_BUTTON = 'ats-job-request-update-success-edit-button',
  TITLE_INPUT = 'ats-job-request-update-title-input',
}

export enum ATSTabbedJobViewVacanciesTabTestIds {
  DISTRICT_VACANCIES = 'ats-tabbed-job-view-district-vacancies',
  DISTRICT_VACANCIES_TITLE = 'ats-tabbed-job-view-district-vacancies-title',
  HRBP_ROW = 'ats-tabbed-job-view-hrbp-row',
  HRBP_SECTION = 'ats-tabbed-job-view-hrbp-section',
  HRBP_TITLE = 'ats-tabbed-job-view-hrbp-title',
  NUMBER_OF_DISTRICT_VACANCIES = 'ats-tabbed-job-view-number-district-vacancies',
  NUMBER_OF_VACANCIES_CHIP = 'ats-tabbed-job-view-vacancies-chip',
  SCHOOL_VACANCIES_COLUMN_TITLE = 'ats-tabbed-job-view-school-vacancies-column-title',
  SCHOOL_VACANCIES_TOOLTIP = 'ats-tabbed-job-view-school-vacancies-tooltip',
  VACANCIES_HEADER = 'ats-tabbed-job-view-vacancies-header',
  VACANCIES_LOADING_SPINNER = 'ats-tabbed-job-view-vacancies-tab-loading-spinner',
  VACANCIES_TABLE = 'ats-tabbed-job-view-vacancies-table',
  VACANCIES_TAB_TITLE = 'ats-tabbed-job-view-vacancies-tab-title',
}

export enum ATSTabbedJobViewQuestionsTabTestIds {
  QUESTIONS_FOR_CANDIDATE_TITLE = 'ats-tabbed-job-view-questions-for-candidate',
  QUESTIONS_COUNT_CHIP = 'ats-tabbed-job-view-questions-count-chip',
  MORE_THAN_TEN_QUESTIONS_WARNING = 'ats-tabbed-job-view-questions-warning',
  QUESTION_SET = 'ats-tabbed-job-view-question-set',
  INDIVIDUAL_QUESTION = 'ats-tabbed-job-view-individual-question',
  LOADING_SPINNER = 'ats-tabbed-job-view-loading-spinner',
}

export enum ATSQuestionComponentTestIds {
  ATTACHMENT_ICON = 'ats-question-component-attachment-icon',
  ALL_QUESTIONS_CHIP = 'ats-question-component-all-questions-chip',
  DIRECTION_TEXT = 'ats-question-component-direction-text-icon',
  MULTIPLE_CHOICE = 'ats-question-component-multiple-choice-icon',
  NIMBLE_QUESTION = 'ats-question-component-nimble-question-icon',
  OPEN_RESPONSE = 'ats-question-component-open-response-icon',
  QUESTION_ICON_CONTAINER = 'ats-question-icon-container',
  QUESTION_SET_TITLE = 'ats-question-question-set-title',
  QUESTION_TEXT = 'ats-tabbed-job-view-question-text',
  QUESTION_TYPE = 'ats-tabbed-job-view-question-type',
  SCHOOL_PREFERENCES = 'ats-question-component-school-preferences-icon',
  STATEMENT_CHECKBOX = 'ats-question-component-statement-checkbox-icon',
  TEXT_QUESTION = 'ats-question-component-text-question-icon',
  VIDEO_LINK = 'ats-question-component-video-link-icon',
  YES_NO_QUESTION = 'ats-question-component-yes-no-question-icon',
}

export enum ATSTabbedJobViewHiringTabTestIds {
  HIRING_TEAM_ALERT = 'ats-tabbed-job-view-hiring-team-alert',
  HIRING_TEAM_LOADING_SPINNER = 'ats-tabbed-job-view-hiring-team-loading-spinner',
  HIRING_TEAM_SECTION_TITLE = 'ats-tabbed-job-view-hiring-team-section-title',
  HIRING_TEAM_USER_DISPLAY = 'ats-tabbed-job-view-hiring-team-user-display',
  HIRING_TEAM_USER_TYPE_LABEL = 'ats-tabbed-job-view-hiring-team-user-type-label',
  NOTIFICATIONS_ALERT = 'ats-tabbed-job-view-notifications-alert',
  NOTIFICATIONS_ALERT_CONDITION_DESCRIPTION = 'ats-tabbed-job-view-notifications-alert-condition-description',
  NOTIFICATIONS_LOADING_SPINNER = 'ats-tabbed-job-view-notifications-loading-spinner',
  NOTIFICATIONS_READY_TO_BE_CLOSED_TEXT = 'ats-tabbed-job-view-notifications-ready-to-be-closed-text',
  NOTIFICATIONS_SECTION_TITLE = 'ats-tabbed-job-view-notifications-section-title',
  NOTIFICATIONS_SELECT_USERS_TEXT = 'ats-tabbed-job-view-notifications-select-users-text',
  NOTIFICATIONS_SUBSCRIBER = 'ats-tabbed-job-view-notifications-subscriber',
  NOTIFICATIONS_SWITCH = 'ats-tabbed-job-view-notifications-switch',
  NOTIFICATIONS_SWITCH_LABEL = 'ats-tabbed-job-view-notifications-switch-label',
  SCORECARD_ALERT = 'ats-tabbed-job-view-scorecard-alert',
  SCORECARD_CHIP = 'ats-tabbed-job-view-scorecard-chip',
  SCORECARD_LOADER = 'ats-tabbed-job-view-scorecard-loader',
  SCORECARD_LOADING_SPINNER = 'ats-tabbed-job-view-scorecard-loading-spinner',
  SCORECARD_SECTION_TITLE = 'ats-tabbed-job-view-scorecard-section-title',
  SCORECARD_TABLE_COLUMN_HEADER = 'ats-tabbed-job-view-scorecard-table-column-header',
  SCORECARD_TABLE_CONTAINER = 'ats-tabbed-job-view-scorecard-table-container',
  SCORECARD_TABLE_ROW = 'ats-tabbed-job-view-scorecard-table-row',
}

export enum ATSJobPostingTabTestIds {
  BENEFITS_ACCORDION = 'ats-job-posting-tab-benefits-accordion',
  BENEFITS_TEXT = 'ats-job-posting-tab-benefits',
  CONTACT_TEXT = 'ats-job-posting--tab-contact-text',
  DEADLINE_TEXT = 'ats-job-posting-tab-deadline-text',
  DESCRIPTION_ACCORDION = 'ats-job-posting-tab-description-accordion',
  DESCRIPTION_AND_BENEFITS_CONTAINER = 'ats-job-posting-tab-description-benefits-container',
  DESCRIPTION_TEXT = 'ats-job-posting-tab-description',
  DETAILS_SECTION = 'ats-job-posting-tab-details-section',
  ERROR_TEXT = 'ats-job-posting-tab-error-text',
  JOB_DETAILS_TITLE = 'ats-job-posting-tab-job-details-title',
  GRADES_TEXT = 'ats-job-posting-tab-grades-text',
  JOB_BOARD_TEXT = 'ats-job-posting-tab-job-board-text',
  LOADING_SPINNER = 'ats-job-posting-tab-loading-spinner',
  PUBLIC_SHARE_LINK = 'ats-job-posting-tab-public-share-link',
  SALARY_TEXT = 'ats-job-posting-tab-salary-text',
  SCHOOLS_TEXT = 'ats-job-posting-tab-schools-text',
  START_DATE_TEXT = 'ats-job-posting-tab-start-date-text',
  SUBJECTS_TEXT = 'ats-job-posting-tab-subjects-text',
}

export enum ATSTabbedJobViewSetUpTabTestIds {
  APPLICATION_SECTIONS_SETTINGS = 'ats-tabbed-job-view-job-setup-tab-settings',
  CANDIDATE_SETTINGS_SECTION_TITLE = 'ats-tabbed-job-view-candidate-settings-section',
  EXTERNAL_CANDIDATE_SETTINGS = 'ats-tabbed-job-view-job-setup-tab-external-candidate-settings',
  INTERNAL_CANDIDATE_SETTINGS = 'ats-tabbed-job-view-job-setup-tab-internal-candidate-settings',
  INTERNAL_OR_EXTERNAL = 'ats-tabbed-job-view-job-setup-tab-internal-or-external',
  JOB_PASSWORD = 'ats-tabbed-job-view-job-setup-tab-password',
  JOB_SETTINGS_TITLE = 'ats-tabbed-job-view-job-setup-tab-job-settings-title',
  LOADING_SPINNER = 'ats-tabbed-job-view-job-setup-tab-loading-spinner',
  OPEN_EXTERNAL_DATE = 'ats-tabbed-job-view-job-setup-tab-open-external-date',
  NOT_OPEN_EXTERNAL_CANDIDATES_TEXT = 'ats-tabbed-job-view-not-open-external-candidates-text',
  REQUISITION_NUMBER = 'ats-tabbed-job-view-job-setup-tab-requisition-number',
  SETTINGS_ERROR_ALERT = 'ats-tabbed-job-view-setup-tab-error-alert',
  SETTINGS_TOGGLE = 'ats-tabbed-job-view-job-setup-tab-settings-toggle',
  STATUS_WORKFLOW_LIST = 'ats-tabbed-job-view-job-setup-tab-status-workflow-list',
  STATUS_WORKFLOW_TITLE = 'ats-tabbed-job-view-job-setup-tab-status-workflow-title',
  VISIBILITY_TITLE = 'ats-tabbed-job-view-job-setup-tab-visibility-title',
}

export enum ATSTabbedJobViewInternalRoleNotesTestIds {
  DELETE_NOTE_BUTTON = 'ats-tabbed-job-view-internal-role-notes-delete-note-button',
  EDIT_NOTE_BUTTON = 'ats-tabbed-job-view-internal-role-notes-edit-note-button',
  NOTE_FOOTER_TEXT = 'ats-tabbed-job-view-internal-role-notes-note-footer-text',
  EXISTING_NOTE_CONTENT = 'ats-tabbed-job-view-internal-role-notes-existing-note-content',
  NOTE_INPUT = 'ats-tabbed-job-view-internal-role-notes-note-input',
  NOTE_INPUT_CONTAINER = 'ats-tabbed-job-view-internal-role-notes-note-input-container',
  FETCH_USERS_ERROR_ALERT = 'ats-tabbed-job-view-internal-role-notes-fetch-users-error-alert',
  CANCEL_EDIT_NOTE_BUTTON = 'ats-tabbed-job-view-internal-role-notes-cancel-edit-note-button',
  SAVE_EDIT_NOTE_BUTTON = 'ats-tabbed-job-view-internal-role-notes-save-edit-note-button',
  EDITABLE_NOTE = 'ats-tabbed-job-view-internal-role-notes-editable-note',
  NOTE_LIST_ERROR_ALERT = 'ats-tabbed-job-view-internal-role-notes-note-list-error-alert',
  NO_NOTES_INFO = 'ats-tabbed-job-view-internal-role-notes-no-notes-info',
  EXPAND_NOTES_BUTTON = 'ats-tabbed-job-view-internal-role-notes-expand-notes-button',
  NOTES_LENGTH_CHIP = 'ats-tabbed-job-view-internal-role-notes-notes-length-chip',
  EXISTING_NOTES_SECTION_TITLE = 'ats-tabbed-job-view-internal-role-notes-existing-notes-section-title',
  EDIT_NOTE_ERROR_SNACKBAR = 'ats-tabbed-job-view-internal-role-notes-edit-note-error-snackbar',
  DELETE_NOTE_ERROR_SNACKBAR = 'ats-tabbed-job-view-internal-role-notes-delete-note-error-snackbar',
  ADD_NOTE_SECTION_TITLE = 'ats-tabbed-job-view-internal-role-notes-add-note-section-title',
  ADD_NOTE_BUTTON = 'ats-tabbed-job-view-internal-role-notes-add-note-button',
  ADD_NOTE_HELP_TEXT = 'ats-tabbed-job-view-internal-role-notes-add-note-help-text',
  ADD_NOTE_ERROR_SNACKBAR = 'ats-tabbed-job-view-internal-role-notes-add-note-error-snackbar',
  NOTE_LIST_LOADER = 'ats-tabbed-job-view-internal-role-notes-note-list-loader',
}

export enum ATSTabbedJobViewAutomationsTabTestIds {
  AUTOMATIONS_CONTENT = 'ats-tabbed-job-view-automations-tab-content',
  AUTOMATIONS_TITLE = 'ats-tabbed-job-view-automations-tab-title',
  CREATE_AUTOMATION_BUTTON = 'ats-tabbed-job-view-automations-tab-create-automation-button',
  EDIT_AUTOMATION_BUTTON = 'ats-tabbed-job-view-automations-tab-edit-automation-button',
  LOADING_SPINNER = 'ats-tabbed-job-view-automations-tab-loading-spinner',
}

export enum ATSTabbedJobViewActivityFeedTestIds {
  ACTIVITY_FEED = 'ats-tabbed-job-view-activity-feed-section-activity-feed',
  LOADING_SPINNER = 'ats-tabbed-job-view-activity-feed-section-loading-spinner',
}

export enum ATSTabbedJobViewNavigationTestIds {
  BACK_BUTTON = 'ats-tabbed-job-view-back-button',
  CANDIDATES_BUTTON = 'ats-tabbed-job-view-candidates-button',
  CLOSE_MOBILE_NAVIGATION_DRAWER = 'ats-tabbed-job-view-close-mobile-navigation-drawer',
  DESKTOP_NAVIGATION = 'ats-tabbed-job-view-desktop-navigation',
  DESKTOP_TAB = 'ats-tabbed-job-view-desktop-tab',
  HAMBURGER_BUTTON = 'ats-tabbed-job-view-hamburger-button',
  MOBILE_CANDIDATES_BUTTON = 'ats-tabbed-job-view-mobile-candidates-button',
  MOBILE_DRAWER_TITLE = 'ats-tabbed-job-view-mobile-drawer-title',
  MOBILE_NAVIGATION = 'ats-tabbed-job-view-mobile-navigation',
  MOBILE_TAB = 'ats-tabbed-job-view-mobile-tab',
  MOBILE_TITLE = 'ats-tabbed-job-view-mobile-title',
}

export enum ATSEmailTemplateListTestIds {
  SAVE_TEMPLATE_SUCCESS_SNACKBAR = 'ats-email-template-list-save-template-success-snackbar',
  SAVE_TEMPLATE_ERROR_SNACKBAR = 'ats-email-template-list-save-template-error-snackbar',
}

export enum ATSDistrictJobsListTestIds {
  HEADER_SECTION_SUCCESS_SNACKBAR = 'ats-district-jobs-list-header-section-success-snackbar',
  HEADER_SECTION_ERROR_SNACKBAR = 'ats-district-jobs-list-header-section-error-snackbar',
  SUCCESS_SNACKBAR = 'ats-district-job-board-job-list-success-snackbar',
  ERROR_SNACKBAR = 'ats-district-job-board-job-list-error-snackbar',
}

export enum ATSJobItemsListTestIds {
  CREATE_TEMPLATE_SUCCESS_SNACKBAR = 'ats-job-items-list-create-template-success-snackbar',
  DELETE_TEMPLATE_SUCCESS_SNACKBAR = 'ats-job-items-list-delete-template-success-snackbar',
  ERROR_SNACKBAR = 'ats-job-items-list-error-snackbar',
  HEADER_SECTION_SUCCESS_SNACKBAR = 'ats-job-items-list-header-section-success-snackbar',
  HEADER_SECTION_ERROR_SNACKBAR = 'ats-job-items-list-header-section-error-snackbar',
}

export enum ATSSocialMediaShareTestIds {
  ERROR_SNACKBAR = 'ats-social-media-share-error-snackbar',
}

export enum ATSCandidateBulkExportTestIds {
  CATEGORY_TITLE = 'ats-candidate-bulk-export-category-title',
  CATEGORY_TITLE_CHECKBOX = 'ats-candidate-bulk-export-category-title-checkbox',
  CATEGORY_CONTAINER = 'ats-candidate-bulk-export-category-container',
  CANCEL_BUTTON = 'ats-candidate-bulk-export-cancel-button',
  REQUEST_EXPORT_BUTTON = 'ats-candidate-bulk-export-request-export-button',
  SUCCESS_MODAL_CLOSE_BUTTON = 'ats-candidate-bulk-export-success-modal-close-button',
  SUCCESS_MODAL_SUBTITLE = 'ats-candidate-bulk-export-success-modal-subtitle',
  SELECTED_FIELDS_CONTROLLER = 'ats-candidate-bulk-export-selected-fields-controller',
  SELECTED_FIELDS_CONTROLLER_CHECKBOX = 'ats-candidate-bulk-export-selected-fields-controller-checkbox',
  ERROR_SNACKBAR = 'ats-candidate-bulk-export-error-snackbar',
  SELECT_FIELDS_MODAL = 'ats-candidate-bulk-export-select-fields-modal',
  SELECT_FIELDS_MODAL_SUBTITLE = 'ats-candidate-bulk-export-select-fields-modal-subtitle',
  CATEGORY_ACCORDION = 'ats-candidate-bulk-export-category-accordion',
  SELECT_FIELDS_MODAL_TITLE = 'ats-candidate-bulk-export-select-fields-modal-title',
  SELECT_FIELDS_MODAL_CLOSE_BUTTON = 'ats-candidate-bulk-export-select-fields-modal-close-button',
  SUCCESS_MODAL_TITLE = 'ats-candidate-bulk-export-success-modal-title',
}

export enum ATSConnectHiringRequestTestIds {
  ACCORDION_TITLE = 'ats-connect-hiring-request-action-accordion-title',
  ACTION_ICON = 'ats-connect-hiring-request-action-icon',
  ACTION_ICON_BUTTON = 'ats-connect-hiring-request-action-icon-button',
  ACTIONS_ACCORDION = 'ats-connect-hiring-request-actions-accordion',
  ACTIONS_EXPAND_MORE_ICON = 'ats-connect-hiring-request-actions-expand-more-icon',
  CANDIDATE_NAME = 'ats-connect-hiring-request-candidate-name',
  CANDIDATE_ROW = 'ats-connect-hiring-request-candidate-row',
  CANDIDATES_LIST = 'ats-connect-hiring-request-candidates-list',
  EXPANDED_CTA_ACTION_ROW = 'ats-connect-hiring-request-action-expanded-cta-row',
  EXPANDED_CTA_CONTAINER = 'ats-connect-hiring-request-actions-expanded-cta-container',
  HIRING_REQUEST_TITLE = 'ats-connect-hiring-request-title',
  JOB_STATUS = 'ats-connect-hiring-request-job-status',
  JOB_TITLE = 'ats-connect-hiring-request-job-title',
  LOAD_MORE_BUTTON = 'ats-connect-hiring-request-load-more-button',
  LOAD_MORE_ERROR = 'ats-connect-hiring-request-load-more-error',
  NUMBER_OF_NOTES = 'ats-connect-hiring-requests-number-of-notes',
  REQUESTOR = 'ats-connect-hiring-request-requestor-name',
  REQUESTOR_DATE = 'ats-connect-hiring-request-requestor-date',
  REQUIRED_ACTIONS_CTA = 'ats-connect-hiring-request-action-required-actions-cta',
  STATUS_CHIP = 'ats-connect-hiring-request-status-chip',
  STATUS_DROPDOWN = 'ats-connect-hiring-request-status-dropdown',
  STATUS_ERROR_SNACKBAR = 'ats-connect-hiring-request-status-error-snackbar',
  UNAUTHORIZED_IMPORT_CANDIDATE_ERROR = 'ats-connect-hiring-request-unauthorized-import-candidate-error',
}

export enum ATSConnectHiringRequestCandidateFeedbackTestIds {
  ADD_NEW_SCORECARD_BUTTON = 'ats-connect-hiring-request-candidate-feedback-add-new-scorecard-button',
  CLOSE_MODAL_LIST_BUTTON = 'ats-connect-hiring-request-candidate-feedback-close-modal-list-button',
  COMPLETION_CHIP = 'ats-connect-hiring-request-candidate-feedback-completion-chip',
  COMPLETION_TEXT = 'ats-connect-hiring-request-candidate-feedback-completion-text',
  CREATE_PANE_CLOSE_BUTTON = 'ats-connect-hiring-request-candidate-feedback-create-pane-close-button',
  DELETE_BUTTON = 'ats-connect-hiring-request-candidate-feedback-delete-button',
  ERROR_TOAST = 'ats-connect-hiring-request-create-feedback-error-toast',
  ERROR_TOAST_ROW_CTA = 'ats-connect-hiring-request-create-feedback-toast-row-cta',
  FEEDBACK_COUNT_CHIP = 'ats-connect-hiring-request-candidate-feedback-number-of-feedback-chip',
  MODAL = 'ats-connect-hiring-request-candidate-feedback-modal',
  MODAL_ROW = 'ats-connect-hiring-request-candidate-feedback-modal-row',
  MODAL_TITLE = 'ats-connect-hiring-request-candidate-feedback-modal-title',
  NO_SCORECARDS_MESSAGE = 'ats-connect-hiring-request-candidate-feedback-no-scorecards-message',
  OVERALL_RECOMMENDATION_TEXT = 'ats-connect-hiring-request-candidate-feedback-overall-recommendation-text',
  RECOMMENDATION_COMPLETED_TEXT = 'ats-connect-hiring-request-candidate-feedback-recommendation-completed-text',
  RECOMMENDATION_SECTION = 'ats-connect-hiring-request-candidate-feedback-recommendation-section',
  REMIND_OR_EDIT_BUTTON = 'ats-connect-hiring-request-candidate-feedback-remind-or-edit-button',
  ROLE_TITLE = 'ats-connect-hiring-request-candidate-feedback-role-title',
  SCORECARDS_LIST_ERROR = 'ats-connect-hiring-request-candidate-feedback-scorecards-list-error',
  SCORECARDS_LIST_LOADING_SPINNER = 'ats-connect-hiring-request-candidate-feedback-scorecards-list-loading-spinner',
  SCORECARD_PROGRESS_SECTION = 'ats-connect-hiring-request-candidate-feedback-section-scorecard-progress',
  SUBMITTER_NAME = 'ats-connect-hiring-request-candidate-feedback-submitter-name',
  SUCCESS_TOAST = 'ats-connect-hiring-request-create-feedback-success-toast',
  TEMPLATE_AND_VISIBILITY = 'ats-connect-hiring-request-candidate-feedback-template-and-visibility',
  TEMPLATE_NAME = 'ats-connect-hiring-request-candidate-feedback-template-name',
  TOTAL_SCORE = 'ats-connect-hiring-request-candidate-feedback-total-score',
  TOTAL_SCORE_SECTION = 'ats-connect-hiring-request-candidate-feedback-section-total-score-status-section',
  VIEW_BUTTON = 'ats-connect-hiring-request-candidate-feedback-section-view-button',
  VISIBILITY = 'ats-connect-hiring-request-candidate-feedback-visibility',
  VISIBILITY_ME_ONLY = 'ats-connect-hiring-request-candidate-feedback-visibility-only-me',
}

export enum ATSConnectHiringRequestCreateFeedbackTestIds {
  CANCEL_BUTTON = 'ats-connect-hiring-request-create-feedback-cancel-button',
  CREATE_PANE_LABEL_TEXT = 'ats-connect-hiring-request-create-feedback-create-pane-label-text',
  CREATE_PANE_TITLE = 'ats-connect-hiring-request-create-feedback-create-pane-title',
  CUMULATIVE_SCORE_QUESTION = 'ats-connect-hiring-request-create-feedback-cumulative-score-question',
  DIRECTION_TEXT_QUESTION = 'ats-connect-hiring-request-create-feedback-direction-text-question',
  EDIT_CANCEL_BUTTON = 'ats-connect-hiring-request-create-feedback-edit-cancel-button',
  FINAL_RECOMMENDATION_QUESTION = 'ats-connect-hiring-request-create-feedback-final-recommendation-question',
  MULTIPLE_CHOICE_QUESTION = 'ats-connect-hiring-request-create-feedback-multiple-choice-question',
  NIMBLE_OR_TEXT_QUESTION = 'ats-connect-hiring-request-create-feedback-nimble-or-text-question',
  QUESTION_SUB_FORM = 'ats-connect-hiring-request-create-feedback-question-sub_form',
  SAVE_AS_DRAFT_BUTTON = 'ats-connect-hiring-request-create-feedback-save-as-draft-button',
  SCORECARD_QUESTION = 'ats-connect-hiring-request-create-feedback-scorecard-question',
  SELECT_LOADING_SKELETON = 'ats-connect-hiring-request-create-feedback-loading-spinner',
  SELECTED_TEMPLATE_TITLE = 'ats-connect-hiring-request-create-feedback-question-selected-template-title',
  SUBMIT_BUTTON = 'ats-connect-hiring-request-create-feedback-submit-button',
  SUCCESS_TOAST = 'ats-connect-hiring-request-create-feedback-success-toast',
  TEMPLATE_SELECT = 'ats-connect-hiring-request-create-feedback-template-select',
}

export enum ATSConnectHiringRequestCreateReferenceContactIds {
  ADD_CONTACT_BACK_BUTTON = 'ats-connect-hiring-request-add-new-reference-add-contact-back-button',
  ADD_CONTACT_BUTTON = 'ats-connect-hiring-request-create-reference-contact-add-contact-button',
  ADD_CONTACT_FIELD = 'ats-connect-hiring-request-add-new-reference-add-contact-field',
  ADD_CONTACT_REQUIRED_FIELD_STAR = 'ats-connect-hiring-request-add-new-reference-add-contact-required-field-star',
  ADD_CONTACT_SAVE_AND_CONTINUE_BUTTON = 'ats-connect-hiring-request-add-new-reference-add-contact-save-and-continue-button',
  ADD_NEW_CONTACT_LINK = 'ats-connect-hiring-request-create-reference-add-new-contact-link',
  ADD_NEW_CONTACT_TITLE = 'ats-connect-hiring-request-create-reference-contact-new-contact-title',
  ADD_NEW_REFERENCE_CONTACT_FORM_SECTION = 'ats-connect-hiring-request-add-new-reference-contact-form-section',
  ADD_NEW_REFERENCE_CONTACT_FORM_TITLE = 'ats-connect-hiring-request-add-new-reference-contact-form-title',
  CANCEL_BUTTON = 'ats-connect-hiring-request-create-reference-contact-cancel-button',
  ERROR_TOAST = 'ats-connect-hiring-request-create-reference-error-toast',
  LANDING_PAGE = 'ats-connect-hiring-request-create-reference-create-landing-page',
  NEW_REFERENCE_CHECK_FOR_TITLE = 'ats-connect-hiring-request-create-reference-new-reference-check-for-title',
  SUCCESS_TOAST = 'ats-connect-hiring-request-create-reference-success-toast',
}

export enum ATSConnectHiringRequestRequestReferenceTestIds {
  CANCEL_BUTTON = 'ats-connect-hiring-request-request-reference-cancel-button',
  REFERENCE_CONTACT_SELECT = 'ats-connect-hiring-request-request-reference-contact-select',
  REFERENCE_TEMPLATE_SELECT = 'ats-connect-hiring-request-request-reference-template-select',
  SUBMIT_BUTTON = 'ats-connect-hiring-request-request-reference-submit-button',
  TOGGLE_BUTTONS = 'ats-connect-hiring-request-request-reference-toggle-button',
  EMAIL_TEMPLATE_SELECT = 'ats-connect-hiring-request-request-reference-email-template-select',
  ERROR_TOAST_REQUEST_REFERENCE = 'ats-connect-hiring-request-request-reference-error-toast-request-reference',
  REFERENCE_QUESTION = 'ats-connect-hiring-request-request-reference-question',
}

export enum ATSCredentialsWithVerificationTestIds {
  CREDENTIAL_EXPLANATION = 'ats-credential-verification-explanation',
  CREDENTIAL_LIST = 'ats-credential-verification-list',
  CREDENTIAL_LIST_ITEM = 'ats-credential-verification-list-item',
  CREDENTIALS_LIST_TITLE = 'ats-credential-verification-list-title',
  ERROR_ALERT = 'ats-credential-verification-error-alert',
  LOADING_SPINNER = 'ats-credential-verification-loading-spinner',
  NO_CREDENTIALS_ALERT = 'ats-credential-verification-no-credentials-alert',
  UNVERIFIED_BULLET_POINT = 'ats-credential-verification-bullet-point-unverified',
  UNVERIFIED_CREDENTIAL = 'ats-credential-verification-list-item-unverified',
  VERIFIED_CREDENTIAL = 'ats-credential-verification-list-item-verified',
  VERIFIED_ICON = 'ats-credential-verification-icon-verified',
  VERIFICATION_SUBTEXT = 'ats-credential-verification-subtext',
  VIEW_CREDENTIAL_BUTTON = 'ats-credential-verification-view-credential-button',
  CREDENTIAL_MODAL_SCREENSHOT = 'ats-credential-verification-credential-detail-modal-screenshot',
  CREDENTIAL_MODAL_CLOSE_BUTTON = 'ats-credential-verification-credential-detail-modal-close-button',
  VERIFIED_CREDENTIAL_DETAIL_CONTAINER = 'ats-credential-verification-verified-credential-detail-container',
  MANUALLY_VERIFIED_TEXT = 'ats-credential-verification-manually-verified-text',
}

export enum ATSAutomationModalTestIds {
  HEADER_TEXT = 'ats-automation-modal-header-text',
  MODAL = 'ats-automation-modal',
}

export enum ATSAutomationCreateTestIds {
  ACTION_TYPE_DROPDOWN = 'ats-automation-create-action-type-dropdown',
  ANSWER_OPTION_DROPDOWN = 'ats-automation-create-answer-option-dropdown',
  APPLY_AUTOMATION_TEXT = 'ats-automation-create-apply-automation-text',
  AUTOMATION_COUNT_TEXT = 'ats-automation-create-apply-automation-count-text',
  AUTOMATION_SKELETON = 'ats-automation-create-automation-skeleton',
  CLOSE_BUTTON = 'ats-automation-create-close-button',
  CREATE_PANE = 'ats-automation-create-pane',
  DISCARD_BUTTON = 'ats-automation-create-select-discard-button',
  EMAIL_ERROR_MESSAGE = 'ats-automation-create-email-error-message',
  EMAIL_LOADING_SKELETON = 'ats-automation-create-email-loading-skeleton',
  EMAIL_TEMPLATE_SELECT = 'ats-automation-create-email-templace-select',
  EMAIL_SENDER_SELECT = 'ats-automation-create-email-sender-select',
  INDIVIDUAL_AUTOMATION = 'ats-automation-create-individual-automation',
  MISSING_FIELD_ERROR_TEXT = 'ats-automation-create-missing-field-error-text',
  MISSING_QUESTION_ERROR_TEXT = 'ats-automation-create-missing-question-error-text',
  NEW_STATUS_DROPDOWN = 'ats-automation-create-new-status-dropdown',
  SAVE_BUTTON = 'ats-automation-create-save-button',
  SELECT_QUESTION_COMBOBOX = 'ats-automation-create-select-question-combobox',
}

export enum ATSAutomationListTestIds {
  ADD_ANOTHER_AUTOMATION_LINK = 'ats-automation-list-add-another-automation-button',
  CANDIDATE_ANSWER = 'ats-automation-list-candidate-answer',
  CLOSE_BUTTON = 'ats-automation-close-button',
  DELETE_AUTOMATIONS_BUTTON = 'ats-automation-list-delete-automations-button',
  EDIT_AUTOMATIONS_BUTTON = 'ats-automation-list-edit-automations-button',
  LIST_PANE = 'ats-automation-list-pane',
  LIST_SKELETON = 'ats-automation-list-skeleton',
  NEW_STATUS_CHIP = 'ats-automation-new-status-chip',
  SUCCESS_TOAST = 'ats-automation-success-toast',
  ERROR_TOAST = 'ats-automation-error-toast',
}

export enum ATSReferenceCheckDataTestIds {
  REFERENCE_CHECK_SECTION = 'ats-reference-check-section',
  REFERENCE_CHECK_TITLE = 'ats-reference-check-title',
  REFERENCE_CHECK_DESCRIPTION = 'ats-reference-check-description',
  SET_UP_AUTO_REFERENCE_BUTTON = 'ats-reference-check-set-up-auto-reference-button',
  REFERENCE_CHECK_MODAL = 'ats-reference-check-modal',
  REFERENCE_CHECK_MODAL_TITLE = 'ats-reference-check-modal-title',
  EMAIL_TEMPLATE_SELECT = 'ats-reference-check-email-template-select',
  FROM_USER_SELECT = 'ats-reference-check-from-user-select',
  REFERENCE_TEMPLATE_SELECT = 'ats-reference-check-template-select',
  APPLICATION_STATUS_SELECT = 'ats-reference-check-status-select',
  SUCCESS_TOAST = 'ats-reference-check-success-toast',
  ERROR_TOAST = 'ats-reference-check-error-toast',
  CANCEL_BUTTON = 'ats-reference-check-cancel-button',
  CREATE_BUTTON = 'ats-reference-check-create-button',
  SAVE_BUTTON = 'ats-reference-check-save-button',
  DELETE_BUTTON = 'ats-reference-check-delete-button',
  EDIT_BUTTON = 'ats-reference-check-edit-button',
}
